import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EMPTY_FIELD } from 'support/helpers/const/const';
import { getCountryNameByCode } from 'support/helpers/countries/countries';
import { PartyDTO } from 'support/types';

type PartyDisplayProps = {
  party: PartyDTO;
};

export const PartyDisplay = ({ party }: PartyDisplayProps) => {
  const { t } = useTranslation();
  const countryName = useMemo(() => {
    if (party.country_code) {
      return getCountryNameByCode(party.country_code);
    }
    return '';
  }, [party.country_code]);

  const gln = useMemo(() => getPartyIdentifier(party, 'GS1'), [party]);
  const vatId = useMemo(() => getPartyIdentifier(party, 'vatID'), [party]);

  const cityStateDisplay = useMemo(() => {
    const parts = [];

    if (party.postal_code) {
      parts.push(party.postal_code);
    }

    if (party.city) {
      parts.push(party.city);
    }

    if (party.state) {
      return parts.join(' ') + `, ${party.state}`;
    }

    return parts.join(' ');
  }, [party.postal_code, party.city, party.state]);

  return (
    <div className="text-sm leading-5 text-gray-700">
      <p>{party.street}</p>
      {cityStateDisplay && <p>{cityStateDisplay}</p>}
      {countryName && <p>{countryName}</p>}
      <div className="space-y-1 pt-2">
        <p>
          <span className="text-gray-500">{t('orderForms:checkout.partyDisplay.labels.gln')}</span> <span>{gln}</span>
        </p>
        <p>
          <span className="text-gray-500">{t('orderForms:checkout.partyDisplay.labels.vatId')}</span>
          <span>{vatId}</span>
        </p>
      </div>
    </div>
  );
};

const getPartyIdentifier = (party: PartyDTO, domain: 'GS1' | 'vatID' | 'internal'): string => {
  return party.identifiers?.find((id) => id.domain === domain)?.identifier ?? EMPTY_FIELD;
};
